import React, { useState, useRef, useEffect } from 'react';
import { useHandleClickOutside } from '../hooks';
import getFloatingSelectPosition from './floatingSelect/getFloatingSelectPosition.js';
import Portal from './Portal.js';
import { useSelector } from 'react-redux';
import selectors from '../selectors/selectors.js';
import SVG from '../images/svg/SVG.js';

const FloatingSelect = (props) => {
  const isMobile = useSelector(selectors.mobile);

  const [search, setSearch] = useState('');
  const [optionsIndex, setOptionsIndex] = useState(0);
  const [xActive, setActive] = useState(false);
  const [trigger, setTrigger] = useState(undefined);
  const optionsRef = useRef(null);
  const searchRef = useRef(null);
  const floatingSelectRef = useRef(null);

  const id = useRef(undefined);
  if (!id.current) {
    id.current = `floating-select-${Math.floor(Math.random() * 100000)}`;
  }

  const active = props.active !== undefined ? props.active : xActive;

  const values = Array.isArray(props.value) ? props.value : [props.value];

  const activeOptions = props.options.filter((n) => (props.freeform ? !n.id || values.includes(n.id) : values.includes(n.id)));

  useHandleClickOutside(floatingSelectRef, () => {
    setActive((state) => (state === true ? false : state));
  });

  useEffect(() => {
    setOptionsIndex(props.optionsIndex);
  }, [props.optionsIndex]);

  useEffect(() => {
    if (active) {
      if (optionsRef.current) {
        optionsRef.current.scrollTop = 0;
      }
      setTimeout(() => {
        if (searchRef.current) {
          searchRef.current.focus();
        }
      }, 50);
    }
  }, [active]);

  useEffect(() => {
    const handleTriggerClick = (e) => {
      setTrigger(e.target);
      setActive(true);
    };
    const handleTriggerFocus = (e) => {
      setTrigger(e.target);
    };
    (props.triggers || []).forEach((n) => {
      setTimeout(() => {
        const elements = typeof n === 'string' ? [...document.querySelectorAll(n)] : [n];
        elements.filter((n) => !!n).forEach((el) => el.addEventListener('click', handleTriggerClick));
        elements.filter((n) => !!n).forEach((el) => el.addEventListener('focus', handleTriggerFocus));
      }, 0);
    });
    return () => {
      (props.triggers || []).forEach((n) => {
        const elements = typeof n === 'string' ? [...document.querySelectorAll(n)] : [n];
        elements.filter((n) => !!n).forEach((el) => el.removeEventListener('click', handleTriggerClick));
        elements.filter((n) => !!n).forEach((el) => el.removeEventListener('focus', handleTriggerFocus));
      });
    };
  }, [props.triggers]);

  let filteredOptions = [...(props.filteredOptions || props.options)];
  if (search) {
    filteredOptions = filteredOptions.filter((n) => String(n.label).toLowerCase().includes(String(search).toLowerCase()));
  }
  filteredOptions = filteredOptions.length === 0 ? [{ label: search ? 'No Results' : 'No Options', id: null }] : filteredOptions;

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setOptionsIndex(0);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setSearch('');
      setActive(false);
    }
  };

  const handleSearchKeyUp = (e) => {
    if (e.key === 'Enter') {
      setSearch('');
      handleOptionClick(filteredOptions[optionsIndex]);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setSearch('');
      setActive(false);
      return;
    }
    let newIndex = (
      {
        ArrowUp: (n) => n - 1,
        ArrowDown: (n) => n + 1,
        PageUp: (n) => n - 7,
        PageDown: (n) => n + 7,
        Home: () => 0,
        End: () => filteredOptions.length - 1,
      }[e.key] || ((n) => n)
    )(optionsIndex);
    newIndex = newIndex < 0 ? 0 : newIndex;
    newIndex = newIndex > filteredOptions.length - 1 ? filteredOptions.length - 1 : newIndex;
    if (newIndex !== optionsIndex) {
      setOptionsIndex(newIndex);
    }
  };

  const handleOptionClick = (option) => {
    setSearch('');
    setOptionsIndex(0);
    setActive(false);
    if (!props.multiple) {
      return props.onChange(option);
    }
    const newOptions = activeOptions.find((n) => n.id === option.id) ? [...activeOptions.filter((n) => (props.freeform ? !n.id || n.id !== option.id : n.id !== option.id))] : [...activeOptions, option];
    props.onChange(newOptions);
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }, 50);
  };

  const isActive = !!props.active || active;
  const position = getFloatingSelectPosition(trigger, optionsRef.current, isMobile) || {};
  const useSearch = !isMobile && props.options.length > 3 && props.search !== false;

  // Todo make efficient
  if (!isActive) {
    //return null;
  }

  return (
    <Portal id={id.current} className={`floating-select-portal ${props.className || ''}`}>
      {isMobile && <div className={`floating-select-backing ${isActive ? 'active' : ''}`} />}
      <div className={`floating-select ${isActive ? 'active' : ''}`} ref={floatingSelectRef} style={{ top: position.top, left: position.left }}>
        <div className={'option-wrapper'} ref={optionsRef}>
          {useSearch && (
            <div className={'search-wrapper'}>
              <input placeholder={props.placeholder || 'Search'} onChange={handleSearchChange} ref={searchRef} data-ignore-escape-key-handler={true} onKeyDown={handleSearchKeyDown} onKeyUp={handleSearchKeyUp} value={search || ''} />
            </div>
          )}
          <div className={'options scrollbar-light'}>
            {filteredOptions.map((option, idx) => (
              <div key={idx} className={`option ${values.includes(option.id) ? 'active' : ''}${idx === optionsIndex ? ' highlighted' : ''}`} onMouseDown={(e) => handleOptionClick(option)}>
                {option.svg && <SVG icon={option.svg} />}
                {option.label}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={`floating-select-placeholder`} />
    </Portal>
  );
};

export default FloatingSelect;
